import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { PlayCircle, BookOpen, BarChart2, Video, HelpCircle, Mail, Send, Loader2, CheckCircle, Upload, FileText } from 'lucide-react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';


const BASE = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || process.env.PUBLIC_URL || '';
const v = (file) => `${BASE}/videos/${file}`.replace(/\/\/+/g, '/');
const POSTER = '/videos/trapound-poster-1280x720.png';

const VideoCard = ({ title, file }) => (
  <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 text-left">
    <h3 className="text-lg font-medium mb-3 text-blue-700 dark:text-blue-400">{title}</h3>
    <div className="relative pb-[56.25%] h-0 rounded-lg overflow-hidden">
      <video
        className="absolute top-0 left-0 w-full h-full"
        controls preload="metadata" poster={POSTER} playsInline
        crossOrigin="anonymous" controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src={v(file)} type="video/mp4" />
        Sorry, your browser doesn’t support embedded videos.
      </video>
    </div>
  </div>
);

const SupportFormContent = ({ user }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({ category: 'Technical Issue', subject: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [file, setFile] = useState(null);

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    if (!executeRecaptcha) return;
    setStatus('loading');

    try {
      const token = await executeRecaptcha('support_form');
      const data = new FormData();
      data.append('category', formData.category);
      data.append('subject', formData.subject);
      data.append('message', formData.message);
      data.append('email', user?.email || 'unknown@trapound.net');
      data.append('recaptchaToken', token);
      if (file) data.append('attachment', file);

      const response = await fetch('https://wixapi.onrender.com/api/support', {
        method: 'POST',
        body: data,
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ category: 'Technical Issue', subject: '', message: '' });
        setFile(null);
      } else {
        throw new Error('Send failed');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-12 rounded-3xl text-center">
        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
        <h3 className="text-3xl font-bold text-green-800 dark:text-green-300 mb-2 text-center">Message Sent!</h3>
        <p className="text-green-700 dark:text-green-400 mb-8 text-lg text-center">We've received your request and will follow up via email.</p>
        <button onClick={() => setStatus('idle')} className="text-blue-600 dark:text-blue-400 font-bold text-lg hover:underline mx-auto">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSupportSubmit} className="space-y-12 bg-gray-50 dark:bg-gray-800/50 p-10 rounded-3xl shadow-inner border border-gray-200 dark:border-gray-700 text-left">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-4">
          <label className="block text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Category</label>
          <select 
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full rounded-2xl border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-4 focus:ring-blue-500/10 py-5 px-6 text-lg transition-all"
          >
            <option>Technical Issue</option>
            <option>Billing / Subscription</option>
            <option>Feature Request</option>
            <option>General Question</option>
          </select>
        </div>
        <div className="space-y-4">
          <label className="block text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Subject</label>
          <input 
            type="text" required value={formData.subject}
            onChange={(e) => setFormData({...formData, subject: e.target.value})}
            placeholder="Brief summary..."
            className="w-full rounded-2xl border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-4 focus:ring-blue-500/10 py-5 px-6 text-lg transition-all"
          />
        </div>
      </div>

      <div className="space-y-4">
        <label className="block text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Detailed Message</label>
        <textarea 
          rows={7} required value={formData.message}
          onChange={(e) => setFormData({...formData, message: e.target.value})}
          placeholder="How can we help you today?"
          className="w-full rounded-2xl border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-4 focus:ring-blue-500/10 py-5 px-6 text-lg transition-all"
        />
      </div>

      <div className="space-y-4">
        <label className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Attachment (Optional)</label>
        <div className="relative border-4 border-dashed border-gray-200 dark:border-gray-700 rounded-3xl p-10 transition-all hover:border-blue-400 hover:bg-blue-50/30 group text-center">
          <input type="file" onChange={(e) => setFile(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
          <Upload className="w-10 h-10 text-gray-300 mx-auto mb-4 group-hover:text-blue-500" />
          <div className="text-gray-500 font-bold">{file ? file.name : "Drop file here or click to browse"}</div>
        </div>
      </div>

      <button
        type="submit" disabled={status === 'loading'}
        className="w-full flex items-center justify-center py-6 px-6 border border-transparent rounded-2xl shadow-xl text-white bg-blue-600 hover:bg-blue-700 transition-all active:scale-[0.98] disabled:opacity-50 text-xl font-black"
      >
        {status === 'loading' ? <><Loader2 className="w-6 h-6 mr-3 animate-spin" /> Sending...</> : <><Send className="w-6 h-6 mr-3" /> Submit Support Ticket</>}
      </button>

      <p className="text-center text-xs text-gray-400 mt-4">
        This site is protected by reCAPTCHA and the Google 
        <a href="https://policies.google.com/privacy" className="underline ml-1">Privacy Policy</a> and 
        <a href="https://policies.google.com/terms" className="underline ml-1">Terms of Service</a> apply.
      </p>
    </form>
  );
};

const HelpPage = () => {
  const [activeTab, setActiveTab] = useState('videos');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (u) => { if (u) setUser(u); });
  }, []);

  // Using full Tailwind color classes to prevent purging
const journalEcosystem = [
  { label: 'Futures', items: ['Retail', 'Backtesting', 'Prop Evaluation', 'Prop Funded'], bullet: 'bg-blue-500' },
  { label: 'Forex', items: ['Retail', 'Backtesting', 'Prop Evaluation', 'Prop Funded'], bullet: 'bg-emerald-500' },
  { label: 'Mixed Assets', items: ['Retail'], bullet: 'bg-slate-500' },
];
  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen p-4 sm:p-6 md:p-8 lg:pl-56 xl:pl-60 relative z-0">
      <div className="max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-3xl shadow-lg p-6 sm:p-8 md:p-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300 pb-2">
            Help & Tutorials
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mt-2 font-medium">Your guide to mastering the trading journal.</p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-12 overflow-x-auto">
          <nav className="-mb-px flex space-x-10">
            {[
              { id: 'videos', label: 'Videos', icon: Video },
              { id: 'faq', label: 'FAQ', icon: HelpCircle },
              { id: 'support', label: 'Support', icon: Mail },
            ].map((tab) => (
              <button
                key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap flex items-center py-5 px-1 border-b-4 font-bold text-xl transition-all ${
                  activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                <tab.icon className="w-6 h-6 mr-3" /> {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* --- Tab Content --- */}
        <div className="text-left">
          {activeTab === 'videos' && (
            <div className="animate-in fade-in duration-500">

              {/* --- NEW SECTION: PRODUCT OVERVIEW --- */}
              <section className="mb-16">
                <div className="flex items-center gap-3 mb-10 border-b dark:border-gray-700 pb-4">
                  <PlayCircle className="w-8 h-8 text-blue-500" />
                  <h2 className="text-3xl font-black dark:text-white">Product Overview</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <VideoCard title="Welcome to Trapound" file="welcome.mp4?v=20260712" />
                </div>
              </section>

              <section className="mb-16">
                <div className="flex items-center gap-3 mb-10 border-b dark:border-gray-700 pb-4">
                  <BookOpen className="w-8 h-8 text-blue-500" />
                  <h2 className="text-3xl font-black dark:text-white">Journal Ecosystem</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
                  {journalEcosystem.map((cat) => (
                    <div key={cat.label} className="bg-gray-50 dark:bg-gray-900/40 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm transition-all hover:shadow-lg">
                      <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b dark:border-gray-700 font-black dark:text-white flex justify-between items-center">
                        {cat.label}
                        <BarChart2 className="w-4 h-4 text-blue-500/50" />
                      </div>
                      <div className="p-6 space-y-3">
                        {cat.items.map(item => (
                          <div key={item} className="flex items-center text-sm font-bold text-gray-600 dark:text-gray-400">
                            {/* Static bullet background for reliability */}
                            <div className={`w-2 h-2 rounded-full mr-3 ${cat.bullet} opacity-50`} /> {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <VideoCard title="Creating a Futures Retail Journal" file="Final_Futures_Music.mp4" />
                  <VideoCard title="Creating a Futures Prop Firm Journal" file="Final_Futures_Prop_Music.mp4" />
                  <VideoCard title="Creating a Forex Retail Journal" file="Final_Forex_Music.mp4" />
                  <VideoCard title="Creating a Forex Prop Firm Journal" file="Final_Forex_Prop_Music.mp4" />
                  <VideoCard title="Creating a Funded Futures Prop Journal" file="Futures Funded.mp4" />
                  <VideoCard title="Creating a Funded Forex Prop Journal" file="Final_Forex_Funded.mp4" />
                  <VideoCard title="Creating a Backtesting Journal" file="Final_Backtesting.mp4" />
                </div>
              </section>

              <section className="mb-16">
                <div className="flex items-center gap-3 mb-10 border-b dark:border-gray-700 pb-4">
                  <BookOpen className="w-8 h-8 text-blue-500" />
                  <h2 className="text-3xl font-black dark:text-white">General Tutorials</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <VideoCard title="How to Enter Trades" file="Final_Journal_Music.mp4" />
                  <VideoCard title="How to Copy Trades" file="Final_Copy_Music.mp4" />
                  <VideoCard title="Understanding Settings" file="Final_Settings_Music.mp4" />
                  <VideoCard title="Stats Overview" file="Stats.mp4" />
                  <VideoCard title="News and Economic Data" file="News.mp4" />
                </div>
              </section>

              <section>
                <div className="flex items-center gap-3 mb-10 border-b dark:border-gray-700 pb-4">
                  <BarChart2 className="w-8 h-8 text-blue-500" />
                  <h2 className="text-3xl font-black dark:text-white">Account Management</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <VideoCard title="Adding and Switching Accounts" file="Final_Switch_Music.mp4" />
                  <VideoCard title="Managing Your Subscription" file="Final_Account_Details.mp4" />
                </div>
              </section>
            </div>
          )}

          {activeTab === 'faq' && (
            <div className="animate-in slide-in-from-bottom-4 duration-500 space-y-10">
              {[
                { 
                  q: "Can I cancel my subscription?", 
                  a: "Yes, you can cancel at anytime. Your subscription will remain active until the next billing cycle." 
                },
                { 
                  q: "What is the difference between the different journal types?", 
                  a: "A Prop Challenge Journal should be used when you are undertaking a funding challenge with a prop firm. In this type of journal, risk calculations are based on the firm's maximum allowed loss, not your total account balance. This is because prop firms don't allow you to risk the entire account balance only the maximum loss amount, and this journaling method helps you manage your risk according to their parameters to avoid violating the rules.\n\nA Prop Funded Journal should be used after you received your funded account from the broker after passing the challenge. Here, risk calculations are typically based on an Effective Balance using the maximum loss amount that the broker allows. For example, you may be given a funded account with a starting balance of $0 and you are allowed have a negative balance of -$2,000 before the account is blown. A starting effective balance of $2,000 will be used for the calculations, once the effective balance falls to $0 or the account balance falls the -$2,000 the account is considered blown.\n\nA Retail Journal should be used for any retail account where you funded it with your own capital. Here, risk calculations are typically based on your actual account balance, reflecting standard portfolio management practices.\n\nA Backtesting Journal should be used when you are backtesting and practicing your strategy on a demo or practice account. This is used with a simulated or paper trading account. The actual balance is calculated for you based on the number of ticks/pips that were won or lost with the trade if an actual balance is not entered. You can also log did not take trade setups so that you can keep them for your notes. Backtesting is an excellent way to fine tune your trading skills and strategy, while also identifying that is working and what is not." 
                },
                { 
                  q: "What is the Mixed Journal type?", 
                  a: "Some brokers allow you to trade multiple asset classes. The mixed journal allows you to support the futures and forex asset classes." 
                },
                { 
                  q: "Why isn't my broker data available for import?", 
                  a: "Only select brokers are available for import. We are continuously adding more. Contact Support with a small test sample of your CSV file for evaluation." 
                },
                { 
                  q: "Why am I not seeing the Copy Trade feature in my settings?", 
                  a: "You must have at least 2 journals to be able to copy trade. Copy trading can only be done within the same account type (e.g., only Futures to Futures)." 
                },
                { 
                  q: "How should I log multiple contract positions where I did partial closes?", 
                  a: "Trapound currently doesn't support splitting trades. You should log the whole trade after all positions are closed or log each position as it is closed." 
                },
                { 
                  q: "How do I log or enter my trades?", 
                  a: "Select the journal on the dashboard and click the 'Entry' button on the sidebar. Check out our 'How to enter trades' video on the Tutorials tab for a walkthrough." 
                },
                { 
                  q: "How do I delete an existing journal?", 
                  a: "On the account card on the Dashboard, click the trash can button. You must have at least one journal active at all times." 
                },
                { 
                  q: "I'm using a Backtesting journal and I'm not seeing any trade psychology triggers?", 
                  a: "Because you are backtesting a strategy, the trading psychology module is disabled as you are not in a live, emotional trading scenario." 
                },
                { 
                  q: "How do I have time to enter all the information on the journal page before the trade is already gone?", 
                  a: "Trapound is designed to be used as a pre-trade planning tool, not something you fill out after the market has already started moving especially trading on lower timeframes.\n\nIn fast markets, you do not need to wait for an exact stop loss price. You can use a reasonable estimate based on your strategy (for example: 10–20 ticks on ES, 20–40 ticks on NQ, or your typical average stop size).\n\nExample workflow:\n• You know your strategy normally uses a ~15 tick stop on ES.\n• You enter 15 ticks once.\n• The calculator instantly shows your contract size.\n• When price approaches your setup, you already know exactly how many contracts to enter.\n\nThis removes hesitation, improves execution speed, and keeps risk consistent.\n\nProfessional traders do their risk math before the trade appears, not while the candle is moving. The goal is preparation — not reaction." 
              },
                { 
                  q: "Why don't I see the Import functionality on the Settings page?", 
                  a: "The Trade Import functionality has been moved directly to the Journal page for easier access while reviewing your trades." 
                },
                { 
                  q: "I have the Free Trial but I can't see all features?", 
                  a: "The Free Trial provides limited access. After the trial ends, you will have access to full projection tables, the Stats page, and funding support tools." 
                },
                { 
                  q: "Why can I no longer create a Stock or Options journal?", 
                  a: "Stock and Options journals are currently not being supported, they are still available for users currently that have them.  No new journals can be created at this time." 
                }
              ].map((item, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 p-8 rounded-3xl border border-gray-100 dark:border-gray-700 text-left">
                  <h3 className="text-xl font-black text-blue-700 dark:text-blue-400 mb-4">{item.q}</h3>
                  <p className="text-gray-700 dark:text-gray-300 font-medium whitespace-pre-wrap leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'support' && (
            <div className="max-w-4xl mx-auto animate-in zoom-in-95 duration-500">
              <GoogleReCaptchaProvider reCaptchaKey="6Le54XcsAAAAACwVZPminQh_6VwGgzAfAwKyTf7r">
                <SupportFormContent user={user} />
              </GoogleReCaptchaProvider>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpPage;